import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CoachLayout } from '../components/CoachLayout';
import { lessonsAPI } from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Download, CheckCircle, XCircle, Calendar, Clock, MapPin, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { SportBadge } from '../components/SportTypes';
import { downloadPDF } from '../lib/pdfDownload';
import { useAuth } from '../context/AuthContext';

export default function LessonDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPro = user?.subscription_plan === 'pro';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDetails();
  }, [id]);

  const fetchDetails = async () => {
    try {
      const response = await lessonsAPI.getDetails(id);
      setData(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dettagli');
      navigate('/lessons');
    } finally {
      setLoading(false);
    }
  };

  const markAsPaid = async (athleteId, paymentType) => {
    try {
      await lessonsAPI.markPayment({ lesson_id: id, athlete_id: athleteId, payment_type: paymentType });
      toast.success(`Pagamento ${paymentType === 'coach' ? 'allenatore' : 'struttura'} marcato come pagato!`);
      fetchDetails();
    } catch (error) {
      toast.error('Errore nel marcare il pagamento');
    }
  };

  const unmarkAsPaid = async (athleteId, paymentType) => {
    try {
      await lessonsAPI.unmarkPayment({ lesson_id: id, athlete_id: athleteId, payment_type: paymentType });
      toast.success(`Pagamento ${paymentType === 'coach' ? 'allenatore' : 'struttura'} annullato!`);
      fetchDetails();
    } catch (error) {
      toast.error('Errore nell\'annullare il pagamento');
    }
  };

  const exportPDF = async () => {
    if (!data) return;
    
    const doc = new jsPDF();
    doc.setFontSize(20);
    doc.text('Dettaglio Lezione', 14, 20);
    
    doc.setFontSize(12);
    doc.text(`Allenatore: ${user?.name || 'N/A'}`, 14, 28);
    doc.text(`Data: ${data.lesson.date}`, 14, 40);
    doc.text(`Ora: ${data.lesson.start_time}`, 14, 47);
    doc.text(`Durata: ${data.lesson.duration_minutes} minuti`, 14, 54);
    doc.text(`Struttura: ${data.lesson.venue_name}`, 14, 61);
    doc.text(`Atleti: ${data.summary.total_athletes}`, 14, 68);
    doc.text(`Totale: € ${data.summary.total_amount.toFixed(2)}`, 14, 75);
    doc.text(`Pagato: € ${data.summary.paid_amount.toFixed(2)}`, 14, 82);
    doc.text(`Da Pagare: € ${data.summary.unpaid_amount.toFixed(2)}`, 14, 89);

    let tableStartY = 100;
    if (isPro && data.lesson.description) {
      doc.setFontSize(10);
      doc.setTextColor(120, 120, 120);
      doc.text('Descrizione', 14, 99);
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(11);
      const lines = doc.splitTextToSize(data.lesson.description, 182);
      doc.text(lines, 14, 106);
      tableStartY = 106 + lines.length * 6 + 6;
    }

    const tableData = data.athletes.map(a => [
      a.athlete_name,
      `€ ${a.coach_amount.toFixed(2)}`,
      a.coach_payment_status === 'paid' ? '✓ PAGATO' : '✗ DA PAGARE',
      `€ ${a.venue_amount.toFixed(2)}`,
      a.venue_payment_status === 'paid' ? '✓ PAGATO' : '✗ DA PAGARE',
      `€ ${a.total_amount.toFixed(2)}`
    ]);

    autoTable(doc, {
      head: [['Atleta', 'Compenso', 'Stato', 'Struttura', 'Stato', 'Totale']],
      body: tableData,
      startY: tableStartY,
      theme: 'grid',
      headStyles: { fillColor: [204, 255, 0], textColor: [0, 0, 0] },
      styles: { fontSize: 9 }
    });
    
    await downloadPDF(doc, `lezione-${data.lesson.date}.pdf`, {
      title: 'Lezione',
      text: 'Report lezione pronto',
    });
    toast.success('Report pronto!');
  };

  const handleEdit = () => {
    navigate(`/lessons?edit=${id}&returnTo=${encodeURIComponent(`/lessons/${id}`)}`);
  };

  const handleDelete = async () => {
    if (!window.confirm('Sei sicuro di voler eliminare questa lezione?')) return;
    try {
      await lessonsAPI.delete(id);
      toast.success('Lezione eliminata');
      navigate('/lessons');
    } catch (error) {
      toast.error('Errore durante l\'eliminazione');
    }
  };

  if (loading) {
    return (
      <CoachLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-[#ccff00] text-xl font-bold">Caricamento...</div>
        </div>
      </CoachLayout>
    );
  }

  if (!data) return null;

  return (
    <CoachLayout>
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 pt-1 pb-4 sm:pt-2 sm:pb-8 md:pt-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
          <div className="flex items-start sm:items-center space-x-3 sm:space-x-4">
            <Button
              onClick={() => navigate('/lessons')}
              variant="ghost"
              className="text-zinc-400 hover:text-white p-2 -ml-2"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-3 mb-2">
                <SportBadge sportType={data.lesson.sport_type} size="md" />
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-zinc-400 text-xs sm:text-sm">
                <span className="flex items-center"><Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />{data.lesson.date}</span>
                <span className="flex items-center"><Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />{data.lesson.start_time}</span>
                <span className="flex items-center truncate"><MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />{data.lesson.venue_name}</span>
                <span className="flex items-center"><Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />{data.summary.total_athletes} atleti</span>
              </div>
            </div>
          </div>
          <Button
            onClick={exportPDF}
            className="bg-[#ccff00] text-black hover:bg-[#b3e600] font-bold rounded-xl text-sm w-full sm:w-auto"
          >
            <Download className="w-4 h-4 mr-2" />
            Esporta PDF
          </Button>
        </div>

        {/* Summary Cards */}
        <div className={`grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 ${isPro && data.lesson.description ? 'mb-2' : 'mb-6 sm:mb-8'}`}>
          <div className="glass-card p-3 sm:p-4 rounded-xl border border-white/10">
            <div className="text-zinc-400 text-sm sm:text-base">Durata</div>
            <div className="text-xl sm:text-3xl font-bold text-white">{data.lesson.duration_minutes} min</div>
          </div>
          <div className="glass-card p-3 sm:p-4 rounded-xl border border-white/10">
            <div className="text-zinc-400 text-sm sm:text-base">Totale</div>
            <div className="text-xl sm:text-3xl font-bold text-[#ccff00]">€ {data.summary.total_amount.toFixed(2)}</div>
            <div className="mt-2 space-y-1 text-xs sm:text-sm text-zinc-400">
              <div>Compensi: € {data.summary.total_coach_amount.toFixed(2)}</div>
              <div>Affitti: € {data.summary.total_venue_amount.toFixed(2)}</div>
            </div>
          </div>
          <div className="glass-card p-3 sm:p-4 rounded-xl border border-white/10">
            <div className="text-zinc-400 text-sm sm:text-base">Pagato</div>
            <div className="text-xl sm:text-3xl font-bold text-green-500">€ {data.summary.paid_amount.toFixed(2)}</div>
            <div className="mt-2 space-y-1 text-xs sm:text-sm text-zinc-400">
              <div>Compensi: € {data.summary.paid_coach_amount.toFixed(2)}</div>
              <div>Affitti: € {data.summary.paid_venue_amount.toFixed(2)}</div>
            </div>
          </div>
          <div className="glass-card p-3 sm:p-4 rounded-xl border border-white/10">
            <div className="text-zinc-400 text-sm sm:text-base">Da Pagare</div>
            <div className="text-xl sm:text-3xl font-bold text-red-500">€ {data.summary.unpaid_amount.toFixed(2)}</div>
            <div className="mt-2 space-y-1 text-xs sm:text-sm text-zinc-400">
              <div>Compensi: € {(data.summary.total_coach_amount - data.summary.paid_coach_amount).toFixed(2)}</div>
              <div>Affitti: € {(data.summary.total_venue_amount - data.summary.paid_venue_amount).toFixed(2)}</div>
            </div>
          </div>
        </div>

        {/* Descrizione — solo Pro */}
        {isPro && data.lesson.description && (
          <div className="glass-card rounded-xl border border-white/10 p-4 mb-2">
            <p className="text-zinc-400 text-xs uppercase tracking-wide mb-1.5">Descrizione</p>
            <p className="text-white text-sm whitespace-pre-wrap">{data.lesson.description}</p>
          </div>
        )}

        {/* Athletes List */}
        <div className="glass-card rounded-xl border border-white/10 overflow-hidden">
          <div className="p-3 sm:p-4 border-b border-white/10">
            <h2 className="text-lg sm:text-xl font-bold text-white">Atleti Partecipanti ({data.athletes.length})</h2>
            {data.lesson.extra_athletes > 0 && (
              <div className="mt-1 text-xs sm:text-sm text-zinc-400">
                Atleti totali in compresenza: {data.summary.total_athletes_in_venue}
              </div>
            )}
          </div>
          
          {data.athletes.length === 0 ? (
            <div className="p-6 sm:p-8 text-center text-zinc-400 text-sm">
              Nessun atleta in questa lezione
            </div>
          ) : (
            <>
              {/* Mobile View - Cards */}
              <div className="sm:hidden divide-y divide-white/5">
                {data.athletes.map((athlete) => (
                  <div key={athlete.athlete_id} className="p-3 space-y-3">
                    <button 
                      onClick={() => navigate(`/athletes/${athlete.athlete_id}`)}
                      className="text-white hover:text-[#ccff00] font-medium text-sm"
                    >
                      {athlete.athlete_name}
                    </button>
                    
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {/* Compenso Allenatore */}
                      <div className="bg-zinc-900/50 rounded-lg p-2.5 space-y-2">
                        <div className="text-zinc-400 text-[10px] uppercase tracking-wide">Compenso Allenatore</div>
                        <div className={`font-mono text-base font-bold ${
                          athlete.coach_payment_status === 'paid' ? 'text-green-500' : 'text-red-500'
                        }`}>€{athlete.coach_amount.toFixed(2)}</div>
                        <div className="flex items-center justify-between">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${athlete.coach_payment_status === 'paid' ? 'bg-green-900/30 text-green-500' : 'bg-red-900/30 text-red-500'}`}>
                            {athlete.coach_payment_status === 'paid' ? '✓ PAGATO' : '✗ DA PAGARE'}
                          </span>
                            {athlete.coach_payment_status === 'paid' ? (
                              <Button size="sm" onClick={() => unmarkAsPaid(athlete.athlete_id, 'coach')} className="bg-green-600 hover:bg-green-700 h-6 w-6 p-0 rounded-lg">
                                <CheckCircle className="w-3 h-3" />
                              </Button>
                            ) : (
                              <Button size="sm" onClick={() => markAsPaid(athlete.athlete_id, 'coach')} className="bg-red-600 hover:bg-red-700 h-6 w-6 p-0 rounded-lg">
                                <XCircle className="w-3 h-3" />
                              </Button>
                            )}
                        </div>
                      </div>
                      
                      {/* Costo Struttura */}
                      <div className="bg-zinc-900/50 rounded-lg p-2.5 space-y-2">
                        <div className="text-zinc-400 text-[10px] uppercase tracking-wide">Costo Struttura</div>
                        <div className={`font-mono text-base font-bold ${
                          athlete.venue_payment_status === 'paid' ? 'text-green-500' : 'text-red-500'
                        }`}>€{athlete.venue_amount.toFixed(2)}</div>
                        <div className="flex items-center justify-between">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${athlete.venue_payment_status === 'paid' ? 'bg-green-900/30 text-green-500' : 'bg-red-900/30 text-red-500'}`}>
                            {athlete.venue_payment_status === 'paid' ? '✓ PAGATO' : '✗ DA PAGARE'}
                          </span>
                            {athlete.venue_payment_status === 'paid' ? (
                              <Button size="sm" onClick={() => unmarkAsPaid(athlete.athlete_id, 'venue')} className="bg-green-600 hover:bg-green-700 h-6 w-6 p-0 rounded-lg">
                                <CheckCircle className="w-3 h-3" />
                              </Button>
                            ) : (
                              <Button size="sm" onClick={() => markAsPaid(athlete.athlete_id, 'venue')} className="bg-red-600 hover:bg-red-700 h-6 w-6 p-0 rounded-lg">
                                <XCircle className="w-3 h-3" />
                              </Button>
                            )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Totale */}
                    <div className="text-center pt-1">
                      <span className="text-zinc-400 text-xs">Totale: </span>
                      <span className="text-[#ccff00] font-mono font-bold">€{athlete.total_amount.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Desktop View - Table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-zinc-900">
                    <tr>
                      <th className="px-4 py-3 text-left text-zinc-400 text-sm whitespace-nowrap">Atleta</th>
                      <th className="px-4 py-3 text-right text-zinc-400 text-sm whitespace-nowrap">Compenso Allenatore</th>
                      <th className="px-4 py-3 text-center text-zinc-400 text-sm whitespace-nowrap">Stato</th>
                      <th className="px-4 py-3 text-right text-zinc-400 text-sm whitespace-nowrap">Costo Struttura</th>
                      <th className="px-4 py-3 text-center text-zinc-400 text-sm whitespace-nowrap">Stato</th>
                      <th className="px-4 py-3 text-right text-zinc-400 text-sm whitespace-nowrap">Totale</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.athletes.map((athlete) => (
                      <tr key={athlete.athlete_id} className="border-b border-white/5 hover:bg-zinc-900/50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <button 
                            onClick={() => navigate(`/athletes/${athlete.athlete_id}`)}
                            className="text-white hover:text-[#ccff00] font-medium"
                          >
                            {athlete.athlete_name}
                          </button>
                        </td>
                        <td className={`px-4 py-3 text-right font-mono whitespace-nowrap ${
                          athlete.coach_payment_status === 'paid' ? 'text-green-500' : 'text-red-500'
                        }`}>€ {athlete.coach_amount.toFixed(2)}</td>
                        <td className="px-4 py-3 text-center whitespace-nowrap">
                          {athlete.coach_payment_status === 'paid' ? (
                            <Button
                              size="sm"
                              onClick={() => unmarkAsPaid(athlete.athlete_id, 'coach')}
                              className="bg-green-600 hover:bg-green-700 h-6 w-6 p-0 rounded-lg"
                            >
                              <CheckCircle className="w-3 h-3" />
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => markAsPaid(athlete.athlete_id, 'coach')}
                              className="bg-red-600 hover:bg-red-700 h-6 w-6 p-0 rounded-lg"
                            >
                              <XCircle className="w-3 h-3" />
                            </Button>
                          )}
                        </td>
                        <td className={`px-4 py-3 text-right font-mono whitespace-nowrap ${
                          athlete.venue_payment_status === 'paid' ? 'text-green-500' : 'text-red-500'
                        }`}>€ {athlete.venue_amount.toFixed(2)}</td>
                        <td className="px-4 py-3 text-center whitespace-nowrap">
                          {athlete.venue_payment_status === 'paid' ? (
                            <Button
                              size="sm"
                              onClick={() => unmarkAsPaid(athlete.athlete_id, 'venue')}
                              className="bg-green-600 hover:bg-green-700 h-6 w-6 p-0 rounded-lg"
                            >
                              <CheckCircle className="w-3 h-3" />
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => markAsPaid(athlete.athlete_id, 'venue')}
                              className="bg-red-600 hover:bg-red-700 h-6 w-6 p-0 rounded-lg"
                            >
                              <XCircle className="w-3 h-3" />
                            </Button>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-[#ccff00] font-mono font-bold whitespace-nowrap">€ {athlete.total_amount.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div className="mt-6 sm:mt-8 flex flex-row gap-3 sm:gap-4 justify-between">
          <Button
            onClick={handleEdit}
            className="flex-1 bg-[#ccff00] text-black hover:bg-[#b3e600] font-bold rounded-xl"
          >
            Modifica
          </Button>
          <Button
            onClick={handleDelete}
            variant="destructive"
            className="flex-1 rounded-xl"
          >
            Elimina
          </Button>
        </div>
      </div>
    </CoachLayout>
  );
}
